import {
  CollapsibleCard,
  Drawer,
  LabelButton,
  useToast,
} from '@teamsnap/snap-ui';
import {
  BalanceChangeSkeleton,
  BalanceChangeSummary,
  CollectBalance,
  InstallmentFieldError,
  OverPaidText,
} from 'components/common/balance-adjustment';
import { ConsentCheckbox } from 'components/registration/v2/PostRegEditDrawer';
import {
  ORDER_ADJUSTMENT_ACTION,
  ORDER_ADJUSTMENT_INSTALLMENT_ACTION,
  useAmplitude,
} from 'hooks/amplitude';
import {
  LoadingType,
  useOrderDetails,
  useOrderDetailsActions,
} from 'hooks/orderDetails';
import { PendingPayment } from 'hooks/postRegEditDrawer';
import { getInstallmentErrors } from 'hooks/postRegEditDrawer/helpers';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { AdjustOrderForm } from './AdjustOrderForm';
import { getPendingPayment, isSaveDisabled } from './helpers';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
}

export type Adjustment = {
  amount: string;
  type: 'add' | 'reduce' | 'payment_schedule';
  note: string;
};

export type BalanceError = {
  error?: string;
  fieldErrors?: Record<string, InstallmentFieldError>;
};

export function AdjustOrderDrawer({ isOpen, handleClose }: Props) {
  const [adjustment, setAdjustment] = useState<Adjustment>({
    amount: null,
    type: 'reduce',
    note: '',
  });
  const [pendingPayment, setPendingPayment] = useState<PendingPayment>();
  const [balanceError, setBalanceError] = useState<BalanceError>({});
  const [accepted, setAccepted] = useState<boolean>(false);
  const [previewLoaded, setPreviewLoaded] = useState<boolean>(false);

  const { order, loading, previewLineItems } = useOrderDetails();
  const {
    getOrder,
    getNotes,
    saveOrderAdjustment,
    getPreviewLineItems,
    clearPreviewLineItems,
  } = useOrderDetailsActions();
  const { toast } = useToast();
  const { amplitudeTrack } = useAmplitude();

  const hasBalanceChange =
    previewLineItems?.currentBalance !== previewLineItems?.newBalance;
  const { amount } = adjustment;

  useEffect(() => {
    // Reset the drawer state every time it is closed
    if (!isOpen) {
      resetData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (adjustment.type === 'payment_schedule') {
      fetchPreview('', true);
      setPreviewLoaded(true);
    } else if (!hasBalanceChange) {
      setPreviewLoaded(false);
      clearPreviewLineItems();
    }
  }, [adjustment.type]);

  async function fetchPreview(
    newAmount: string,
    isPaymentScheduleOnly = false
  ) {
    if (
      !isPaymentScheduleOnly &&
      (!order?.id || !newAmount || parseFloat(newAmount) === 0)
    ) {
      return;
    }
    setAccepted(false);

    try {
      await getPreviewLineItems(order.id, {
        ...adjustment,
        amount: newAmount,
      });
      setPreviewLoaded(true);
    } catch (e) {
      toast({ variant: 'negative', title: e.message });
    }
  }

  useEffect(() => {
    validatePendingPayment();
  }, [pendingPayment]);

  useEffect(() => {
    setPendingPayment(getPendingPayment(previewLineItems));
  }, [previewLineItems]);

  const resetData = () => {
    setAdjustment({ amount: null, type: 'reduce', note: '' });
    clearPreviewLineItems();
    setBalanceError({});
    setPendingPayment(null);
    handleClose();
  };

  const saveAdjustment = async () => {
    if (adjustment.type !== 'payment_schedule' && !amount) return;
    // Validate before saving
    if (!validatePendingPayment()) {
      return;
    }

    try {
      const updatedInstallments = pendingPayment?.installments.map(
        (installment) => ({
          _key: installment._key,
          amount: installment.amount,
          canceled: installment.status === 'canceled',
          scheduledDate: installment.scheduledDate,
          id: installment.id,
        })
      );
      const response = await saveOrderAdjustment(order?.id, adjustment, {
        ...pendingPayment,
        installments: updatedInstallments,
      });

      if (response) {
        toast({ variant: 'success', title: 'Order adjusted.' });
        resetData();
      }
    } catch (e) {
      toast({ variant: 'negative', title: 'Failed to save adjustment.' });
    } finally {
      await getOrder(order?.orderNumber);
      await getNotes(order?.id);
    }
  };

  const validatePendingPayment = (): boolean => {
    const { error, fieldErrors } = getInstallmentErrors({
      pendingPayment,
      previewLineItems,
    });
    setBalanceError({ error, fieldErrors });
    if (error || Object.keys(fieldErrors).length > 0) {
      return false;
    }
    return true;
  };

  const saveDisabled = isSaveDisabled(
    balanceError,
    adjustment,
    order,
    previewLoaded,
    accepted,
    loading[LoadingType.GetPreviewLineItems]
  );

  return (
    <Drawer
      open={isOpen}
      onCloseClick={resetData}
      title="Adjust Order"
      onClickOutside={isOpen ? handleClose : undefined}
      size="large"
      testId="AdjustOrderDrawer--drawer"
      className="sui-p-0"
    >
      <Drawer.Header data-testid="AdjustOrderDrawer--header">
        <span
          data-testid="adjust-order-drawer--header"
          className="sui-heading-sm"
        >
          Adjust Order
        </span>
      </Drawer.Header>
      <Drawer.Content
        data-testid="AdjustOrderDrawer--content"
        className="sui-body sui-p-3"
      >
        <div className="sui-flex sui-flex-col sui-gap-3 sui-w-full">
          <AdjustOrderForm
            order={order}
            adjustment={adjustment}
            setAdjustment={setAdjustment}
            fetchPreview={fetchPreview}
          />
          {loading[LoadingType.GetPreviewLineItems] ? (
            <BalanceChangeSkeleton />
          ) : (
            <CollapsibleCard.Root isDefaultOpen>
              <CollapsibleCard.Header
                title={
                  adjustment?.type === 'payment_schedule'
                    ? 'Balance'
                    : 'Balance Change'
                }
              />
              <CollapsibleCard.Content>
                {
                  <BalanceChangeSummary
                    {...previewLineItems}
                    newBalance={
                      adjustment?.type === 'payment_schedule'
                        ? undefined
                        : previewLineItems?.newBalance
                    }
                    currentBalance={order?.amountRemaining}
                  />
                }
                {previewLineItems?.newBalance > 0 && (
                  <CollectBalance
                    newBalance={previewLineItems.newBalance}
                    updateInstallments={(installments, editType) => {
                      setPendingPayment((prevPayment) => ({
                        ...prevPayment,
                        installments,
                      }));
                      editType &&
                        amplitudeTrack.adjustOrderInstallmentsEdited(
                          editType as ORDER_ADJUSTMENT_INSTALLMENT_ACTION
                        );
                    }}
                    error={balanceError.error}
                    fieldErrors={balanceError.fieldErrors}
                    {...pendingPayment}
                  />
                )}
                {previewLineItems?.newBalance < 0 && <OverPaidText />}
              </CollapsibleCard.Content>
            </CollapsibleCard.Root>
          )}
        </div>
      </Drawer.Content>
      <Drawer.Footer>
        <div className="sui-flex sui-flex-col sui-gap-1 sui-w-full">
          {!loading[LoadingType.GetPreviewLineItems] && previewLoaded && (
            <ConsentCheckbox
              previewLineItems={previewLineItems}
              pendingPayment={pendingPayment}
              accepted={accepted}
              adjustment={adjustment}
              setAccepted={(accepted) => {
                setAccepted(accepted);
              }}
            />
          )}
          <div className="sui-flex sui-gap-2 sui-justify-end">
            <LabelButton
              key={'cancel'}
              labelText="Cancel"
              variantType="secondary"
              type="button"
              data-testid="AdjustOrderDrawer--cancel-button"
              onClick={() => {
                resetData();
                amplitudeTrack.adjustOrderActionClicked(
                  ORDER_ADJUSTMENT_ACTION.CANCEL
                );
              }}
            />
            <LabelButton
              key={'save'}
              disabled={saveDisabled}
              labelText="Save"
              loading={loading[LoadingType.SaveAdjustment]}
              onClick={() => {
                saveAdjustment();
                amplitudeTrack.adjustOrderActionClicked(
                  ORDER_ADJUSTMENT_ACTION.SAVE
                );
              }}
              variantType="primary"
              data-testid="AdjustOrderDrawer--save-button"
            />
          </div>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}
